import _ from 'lodash';
import { useEffect, useMemo } from 'react';
//services
import { getConvertDates, getQuote } from 'services/convert.services';
//reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	getConvertDetails,
	getConvertSchedule,
	getMessage,
	getNewConvert,
	setConvert,
	isLoading,
	upsertConvert,
	setMessage,
	resetConvert,
} from 'state/reducers/convert.reducers';
//components
import { FormsStack } from 'theme/elements/Forms';
import { invalidFields, validationTypes } from 'components/FormCreator/types';
import { setListOfInvalids, validateFields } from 'components/FormCreator/validation';
import WarningDialog from 'components/WarningDialog';
//sections
import ConvertDetailsForm from './ConvertDetailsForm';
//mui
import { Collapse } from '@mui/material';
//constants
import { balanceitem, Convert, convertType, fieldsValidations } from 'constants/convert.constants';
import { Fragment, useState } from 'react';
import { timing, userMessage } from 'constants/ui.constants';
import Footer from '../Footer';
import { getCurrencies } from 'state/reducers/global.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { User } from 'constants/user.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import ScheduleForm from './ScheduleForm';
import WaitingMessage from 'components/WaitingMessage';
import { format } from 'date-fns';

export default function Step1(props: { handleCancel: any }) {
	const t = useAppSelector(selectTranslations);
	const convert: Convert = useAppSelector(getNewConvert);
	const convertDetails = useAppSelector(getConvertDetails);
	const convertSchedule = useAppSelector(getConvertSchedule);
	const userMessage: userMessage = useAppSelector(getMessage);
	const dispatch = useAppDispatch();
	const user: User = useAppSelector(getUserDetails);
	const Currencies = _.keyBy(useAppSelector(getCurrencies), 'id');
	const currenciesList = useAppSelector(getCurrencies);
	const getIsLoading = useAppSelector(isLoading);
	let companyBalancesList: balanceitem[] = useAppSelector(getCompanyBalances);
	const prefixFields = 'page.convert.create.step.1.';
	const [selectedToday, setSelectedToday] = useState(false);

	const allowedCurrencies = useMemo(() => {
		let obj = {};
		try {
			let objCorrencies = _.groupBy(Currencies, 'id');
			companyBalancesList.forEach((item: balanceitem) => {
				if (_.has(objCorrencies, item.currencyId) && item.amount! > 0) {
					obj[objCorrencies[item?.currencyId][0].code] = {
						...objCorrencies[item?.currencyId][0],
						amount: item.amount,
					};
				}
			});
		} catch (e) {}
		return obj;
	}, [companyBalancesList]);

	const isQuoteFormComplete = useMemo(
		() => _.isEmpty(validateFields(fieldsValidations, { ...convertDetails, ...convertSchedule })),
		[convertDetails, convertSchedule],
	);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		let isDecimalAllowed = true;
		let invalidFieldsNew = { ...invalidFields };

		if (_.isEqual(name, 'sell_currency')) {
			dispatch(upsertConvert({ [name]: value, buy_currency: '' }));
		} else {
			dispatch(setConvert({ name: name, value: value }));
		}
		if (_.isEqual(name, 'conversion_date')) {
			setSelectedToday(_.isEqual(value, format(new Date(), timing.DATE_FOR_SERVICE)));
		}

		if (
			(convert.type === 'sell' && name === 'sell_currency') ||
			(convert.type === 'buy' && name === 'buy_currency')
		) {
			const selectedCurrency: any = _.values(allowedCurrencies).find((c: any) => c.code == value);
			if (selectedCurrency) {
				isDecimalAllowed = selectedCurrency.decimalPlaces > 0;
			} else {
				isDecimalAllowed = true;
			}

			if (name === 'sell_currency' && !value) {
				fieldsValidations.sell_currency = fieldsValidations.sell_currency!.filter(
					(fv: any) => fv === validationTypes.requierd
				);
				fieldsValidations.buy_currency = fieldsValidations.buy_currency!.filter(
					(fv: any) => fv === validationTypes.requierd
				);
			}

			if (!isDecimalAllowed) {
				if (!fieldsValidations.amount?.includes(validationTypes.noDecimalAllowed)) {
					fieldsValidations.amount!.push(validationTypes.noDecimalAllowed);
				}
			} else {
				if (fieldsValidations.amount?.includes(validationTypes.noDecimalAllowed)) {
					fieldsValidations.amount = fieldsValidations.amount!.filter(
						(v) => v !== validationTypes.noDecimalAllowed
					);
					invalidFieldsNew = _.omit(invalidFieldsNew, 'amount');
				}
			}
		}

		if (_.has(fieldsValidations, name) && !value && ['buy_currency', 'sell_currency'].includes(name)) {
			fieldsValidations[name] = [validationTypes.requierd];
		}

		let isBuyCurrencyNoDecimalPlaces = false;
		let isSellCurrencyNoDecimalPlaces = false;

		if (
			name === 'buy_currency' ||
			(name === 'type' && value == convertType.sell && convertDetails.buy_currency) ||
			name == 'amount'
		) {
			const currency = name === 'buy_currency' ? value : convertDetails.buy_currency;
			const buyCurrency: any = currenciesList.find((c: any) => c.code === currency);
			isBuyCurrencyNoDecimalPlaces = buyCurrency ? !(buyCurrency.decimalPlaces > 0) : false;
		}
		if (
			name === 'sell_currency' ||
			(name === 'type' && value == convertType.buy && convertDetails.sell_currency) ||
			name == 'amount'
		) {
			const currency = name === 'sell_currency' ? value : convertDetails.sell_currency;
			const sellCurrency: any = _.values(allowedCurrencies).find((c: any) => c.code === currency);
			isSellCurrencyNoDecimalPlaces = sellCurrency ? !(sellCurrency?.decimalPlaces > 0) : false;
		}

		const type = name === 'type' ? value : convertDetails.type;

		switch (type) {
			case convertType.sell: {
				if (isBuyCurrencyNoDecimalPlaces) {
					fieldsValidations.buy_currency!.push(validationTypes.forbiddenCurrency);
				} else {
					fieldsValidations.buy_currency = fieldsValidations.buy_currency!.filter(
						(v) => v !== validationTypes.forbiddenCurrency
					);
				}
				fieldsValidations.sell_currency = fieldsValidations.sell_currency!.filter(
					(v) => v !== validationTypes.forbiddenCurrency
				);
				break;
			}
			case convertType.buy: {
				if (isSellCurrencyNoDecimalPlaces) {
					fieldsValidations.sell_currency!.push(validationTypes.forbiddenCurrency);
				} else {
					fieldsValidations.sell_currency = fieldsValidations.sell_currency!.filter(
						(v) => v !== validationTypes.forbiddenCurrency
					);
				}
				fieldsValidations.buy_currency = fieldsValidations.buy_currency!.filter(
					(v) => v !== validationTypes.forbiddenCurrency
				);
				break;
			}
		}
		if (_.has(fieldsValidations, name)) {
			let invalids: any = {};
			if (
				((name === 'buy_currency' && convertDetails.sell_currency) ||
					(name === 'sell_currency' && convertDetails.buy_currency)) &&
				convertDetails.amount
			) {
				invalids = validateFields(fieldsValidations as any, { ...convertDetails, [name]: value });
			} else if (
				((convertDetails.type == convertType.buy && name == 'buy_currency' && value) ||
					(convertDetails.type === convertType.sell && name == 'sell_currency' && value)) &&
				convertDetails.amount
			) {
				invalids = validateFields(fieldsValidations as any, {
					...convertDetails,
					[name]: value,
					amount: convertDetails.amount,
				});
			} else {
				invalids = setListOfInvalids(invalidFieldsNew, fieldsValidations, name, value);
			}
			setInvalidFields(invalids);
			setInvalidFieldsChange(true);
		} else {
			if (name === 'type') {
				setInvalidFields({});
				setInvalidFieldsChange(true);
				dispatch(resetConvert(value as convertType));
			}
		}
	};
	const handleSubmit = () => {
		const invalidFields = isValidForm;
		if (!_.keys(invalidFields).length && isQuoteFormComplete) {
			let a: Convert = _.clone(convert);
			a.companyId = user.companyId;
			let currency: any | undefined;
			try {
				if (_.isEqual(a.type, convertType.buy)) {
					currency = allowedCurrencies[convertDetails.sell_currency];
				}
			} catch (e) {}
			dispatch(getQuote(a, Currencies, currency));
		} else {
			setInvalidFields(invalidFields);
		}
	};
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});

	const [isValidForm, setIsValidForm] = useState<any>({});
	const [isInvalidFieldsChange, setInvalidFieldsChange] = useState<boolean>(false);

	useEffect(() => {
		// //check fields
		// if (isInvalidFieldsChange) {
		const invalidsValidations = validateFields(fieldsValidations, convertDetails) || {};
		let invalids: any = {};

		if (invalidsValidations.amount && !convertDetails.amount?.length) {
			const { amount, ...rest } = invalidsValidations;
			invalids = rest;
			if (
				(invalids.sell_currency && !convertDetails?.buy_currency?.length) ||
				(invalids.buy_currency && !convertDetails?.sell_currency?.length)
			) {
				const { sell_currency, buy_currency, ...rest } = invalids;
				invalids = rest;
				setInvalidFields({ ...invalids });
			}
		}

		if (!_.get(invalids, 'sell_currency') && _.isNumber(convertDetails.amount)) {
			//check if amount of sell is less than in balance
			try {
				if (_.isEqual(convertDetails.type, convertType.sell)) {
					let sellAmount = allowedCurrencies[convertDetails.sell_currency].amount;
					if (Number(sellAmount) < Number(convertDetails.amount)) {
						setInvalidFields({
							...invalidFields,
							amount: {
								name: 'amount',
								valid: false,
								errorText: _.replace(
									t('Max amount to sell is {0}'),
									'{0}',
									allowedCurrencies[convertDetails.sell_currency].symbol + sellAmount
								),
							},
						});
					}
					setIsValidForm({
						...invalidFields,
						amount: {
							name: 'amount',
							valid: false,
							errorText: _.replace(
								t('Max amount to sell is {0}'),
								'{0}',
								allowedCurrencies[convertDetails.sell_currency].symbol + sellAmount
							),
						},
					});
				} else {
					setInvalidFields({ ...invalidFields, ...invalids });
					setIsValidForm({ ...invalidFields, ...invalids });
				}
			} catch (e) {}
		}
		setInvalidFields({ ...invalidFields, ...invalids });
		setIsValidForm({ ...invalidFields, ...invalids });
		setInvalidFieldsChange(false);
		// }
	}, [convertDetails, isInvalidFieldsChange]);

	useEffect(() => {
		if (!_.isEmpty(convertDetails.sell_currency) && !_.isEmpty(convertDetails.buy_currency)) {
			dispatch(getConvertDates(convertDetails.buy_currency, convertDetails.sell_currency));
		}
	}, [convertDetails.sell_currency, convertDetails.buy_currency]);

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);

	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				title={userMessage.text}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
			/>
			<FormsStack>
				<ConvertDetailsForm invalidFields={invalidFields} handleChange={handleChange} />
				<Collapse in={convert.optionalDates.length > 0 ? true : false}>
					{
						<ScheduleForm
							invalidFields={invalidFields}
							handleChange={handleChange}
							selectedToday={selectedToday}
						/>
					}
				</Collapse>
				<Footer
					handleCancel={props.handleCancel}
					handleBack={() => {}}
					disableSubmit={
						_.keys(isValidForm).length > 0 ||
						_.keys(invalidFields).length > 0 ||
						!isQuoteFormComplete ||
						getIsLoading
					}
					ifBack={false}
					handleSubmit={handleSubmit}
					labelSubmit={t(`${prefixFields}quote_button.text`)}
					labelCancel={t(`${prefixFields}cancel_button.text`)}
				/>
			</FormsStack>
		</Fragment>
	);
}
